import type { CSSProperties } from "react";

export interface BarGraphSegment {
  label: string;
  value: number;
  /** Optional explicit color for the segment (CSS color value). */
  color?: string;
}

export interface BarGraphProps {
  /** Ordered collection of segments that make up the bar. */
  segments: ReadonlyArray<BarGraphSegment>;
  /** Accessible label describing what the bar represents. */
  ariaLabel?: string;
  /**
   * Optional additional class name applied to the root element.
   * Useful for sizing the bar from the parent component.
   */
  className?: string;
}

export const BarGraph = (props: BarGraphProps): React.JSX.Element => {
  const { segments, ariaLabel, className } = props;

  const total = segments.reduce<number>(
    (sum, segment) => sum + (Number.isFinite(segment.value) ? segment.value : 0),
    0,
  );

  const hasPositiveTotal = total > 0;
  const normalizedSegments = hasPositiveTotal ? segments : [];

  const rootClassName = className
    ? `relative flex w-full h-[41px] rounded-lg overflow-hidden bg-gray-200 ${className}`
    : "relative flex w-full h-[41px] rounded-lg overflow-hidden bg-gray-200";

  return (
    <div
      className={rootClassName}
      role="img"
      aria-label={ariaLabel}
    >
      {normalizedSegments.map((segment, index) => {
        const ratio = segment.value > 0 && hasPositiveTotal
          ? segment.value / total
          : 0;
        const widthPercent = Math.max(0, Math.min(1, ratio)) * 100;

        const isFirst = index === 0;
        const isLast = index === normalizedSegments.length - 1;

        const style: CSSProperties = {
          width: `${widthPercent}%`,
          backgroundColor: segment.color,
        };

        if (isFirst && isLast) {
          style.borderRadius = "8px";
        } else if (isFirst) {
          style.borderRadius = "8px 0 0 8px";
        } else if (isLast) {
          style.borderRadius = "0 8px 8px 0";
        }

        return (
          <div
            key={segment.label ?? index}
            className="h-full flex-shrink-0"
            style={style}
            aria-hidden="true"
          />
        );
      })}
    </div>
  );
};

